import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import 'rc-slider/assets/index.css';
import { useGetVocabularyMutation } from '../../../features/authentication/qSetApiSlice';
import PreviewHeader from '../../assignments/pages/MoreActions/components/PreviewHeader';
import PrintContainer from '../../assignments/pages/MoreActions/components/PrintContainer';
import PrintOptions from '../../assignments/pages/MoreActions/components/PrintOptions';
import CustomToast from '../../../components/Toast';
import { useGetPrintOptionsMutation, useUpdatePrintOptionsMutation } from '../../../features/authentication/persistApiSlice';
import { DeStackSetData, StackSetData, ResetSettings } from '../utils/displaySetFunctions';

const DisplaySet = () => {
  const location = useLocation();
  const { setId } = useParams();
  const { setName, languageId } = location.state;
  const [vocabulary, setVocabulary] = useState({});
  const [vocabularyData, setVocabularyData] = useState([]);
  const [fontStyleClass, setFontStyleClass] = useState('times');
  const [displayTopValue, setDisplayTopValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [printOptions, setPrintOptions] = useState({});
  const [originalSettingsData, setOriginalSettingsData] = useState({});
  const [contentHeights, setContentHeights] = useState([]);
  const [hideDuplicate, setHideDuplicate] = useState(false);
  const [imageCompressionValue, setImageCompressionValue] = useState(50);
  const standardPageHeight = 1300;

  const [getPrintOptions] = useGetPrintOptionsMutation();
  const [updatePrintOptions] = useUpdatePrintOptionsMutation();
  const [qSetVocabulary] = useGetVocabularyMutation();

  const getPrintOptionsAsync = async () => {
    const printOptionsResponse = await getPrintOptions().unwrap();

    let options = {
      ...printOptionsResponse, 
      imageCompressionPercent: printOptionsResponse?.imageCompressionPercent > 0 ? printOptionsResponse.imageCompressionPercent : 50
    };

    setOriginalSettingsData(options);
    setPrintOptions({ ...printOptions, ...options });
    if (printOptions?.imageCompressionPercent == 0) updateSettings({ imageCompressionPercent: options.imageCompressionPercent });
  };

  const updatePrintOptionsAsync = async (options) => {
    try {
      await updatePrintOptions(options).unwrap();
    } catch (ex) {
      console.log(ex);
    }
  };

  const getAssignmentVocabularyAsync = async () => {
    setIsLoading(true);
    const options = {
      id: setId
    };
    try {
      const response = await qSetVocabulary(options);
      if (response.error) {
        if (response.error?.data?.error)
          CustomToast(response.error.data.error, 'error');
      } else {
        setVocabulary(response.data);
        setVocabularyData(response.data.vocabularyInfo);
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const handleVocabularySelect = (event) => {
    const { name, checked } = event.target;
    if (name === 'selectAll') {
      const data = vocabularyData?.map((item) => {
        return { ...item, isChecked: checked };
      });
      setVocabularyData(data);
    } else {
      let vocabularyArray = vocabularyData?.map((item) =>
        `vocabulary${item.id}` === name ? { ...item, isChecked: checked } : item
      );
      setVocabularyData(vocabularyArray);
    }
  };

  const handleClearAll = () => {
    const data = vocabularyData?.map((item) => {
      return { ...item, isChecked: false };
    });
    setVocabularyData(data);
  };

  useEffect(() => {
    getPrintOptionsAsync();
    getAssignmentVocabularyAsync();
  }, []);

  useEffect(() => {
    if (contentHeights.length > 0) {
      setHideDuplicate(true);
    }
  }, [contentHeights]);

  const getContentHeight = (id) => {
    const item = contentHeights.find(q => q.id === id);
    return item ? item.height : 0;
  };

  const updateSettings = (data) => {
    setPrintOptions({ ...printOptions, ...data });
    updatePrintOptionsAsync({ ...printOptions, ...data });
  };

  const resetSettings = () => {
    let updatedSettings = ResetSettings(originalSettingsData);
    setPrintOptions(updatedSettings);
    updateSettings(updatedSettings);
  };

  useEffect(() => {
    if (!vocabularyData || vocabularyData?.length === 0 || !contentHeights) return;

    if (printOptions.twoColumn) {
      const updatedData = StackSetData(vocabularyData, { getContentHeight, standardPageHeight });
      setVocabularyData(updatedData);
    } else {
      const updatedData = DeStackSetData(vocabularyData);
      setVocabularyData(updatedData);
    }
  }, [printOptions?.twoColumn, vocabularyData]);

  return (
    <div className="vocabulary-container">
      <div className="vocabulary-header row">
        <div className="col-lg-9">
          <PreviewHeader
            printLabel={'Display Set'}
            isLoading={isLoading}
          />
          <div className="print-content-scroll">
            <PrintContainer
              setName={setName}
              displayTopValue={displayTopValue}
              fontStyleClass={fontStyleClass}
              printOptions={printOptions}
              isLoading={isLoading}
              teacherName={vocabulary?.teacherName}
              vocabularyData={vocabularyData}
              handleVocabularySelect={handleVocabularySelect}
              handleClearAll={handleClearAll}
              setContentHeights={setContentHeights}
              hideDuplicate={hideDuplicate}
            />
          </div>
        </div>
        <div className="col-lg-3">
          <PrintOptions
            setName={setName}
            displayTopValue={displayTopValue}
            setDisplayTopValue={setDisplayTopValue}
            setFontStyleClass={setFontStyleClass}
            imageCompressionValue={imageCompressionValue}
            setImageCompressionValue={setImageCompressionValue}
            printOptions={printOptions}
            isLoading={isLoading}
            updateSettings={updateSettings}
            resetSettings={resetSettings}
          />
        </div>
      </div>
    </div>
  );
};

export default DisplaySet;
